/**
 * exp-split-variants-form (US-AIF.P2-0254-S01 · @UC9433 @UC9434 @UC9435 @UC9436 · @gate money,ai) —
 * FORM 4 BIẾN THỂ CHIA của khoản CHI.
 *
 * USECASE (real_scenarios.md §1.1 nhóm A + §1.2 nhóm B):
 *  - EXP-F-RATIO  (`:59`) — quỹ trả 600 tiền điện; A gánh 40%, B/C/D mỗi người 20%.
 *  - EXP-F-EXCLUDE(`:62`) — quỹ trả 300 tiệc; D không dự ⇒ chỉ A,B,C gánh, mỗi người 100.
 *  - EXP-P-ADVANCE-EQUAL (`:72`) — A ứng túi 400 tiền ăn; mỗi người nợ A 100 (B,C,D hoàn A).
 *  - EXP-P-PAY-FOR-ONE   (`:77`) — A trả hộ 200 tiền thuốc CHO B; B nợ A 200.
 *
 * NGỮ NGHĨA TIỀN (2 NHÓM ĐỐI LẬP — KHÔNG được lẫn):
 *  ┌ F-* (RATIO/EXCLUDE): source `fund_flow_allocated` ⇒ QUỸ trả 100% CASH, **ZERO DebtEntry**, participant
 *  │  chỉ để MINH BẠCH gánh nặng (Σshare = amountMinor). payerMemberId = NULL (contract CẤM khai).
 *  └ P-* (ADVANCE-EQUAL/PAY-FOR-ONE): source `peer` ⇒ MỘT THÀNH VIÊN ứng tiền túi, quỹ KHÔNG chi (CASH net
 *     0), DebtEntry sinh THẬT với chủ nợ = `payerMemberId` KHAI TƯỜNG MINH (KHÔNG suy từ người bấm nút).
 *
 * HÀM THUẦN (DP-3): KHÔNG DB, KHÔNG LLM, KHÔNG Date.now. Money = string minor VND (B1 — no-float, >0).
 * fund_picker / member_picker / multi_choice options do SERVICE nạp (quỹ/thành viên THẬT) — domain chỉ
 * validate value ∈ options (chống IDOR ở LỚP domain; service re-verify RBAC/tenant THẬT lúc confirm).
 *
 * 🔴 KHÔNG TÍNH TIỀN Ở ĐÂY (mirror exp-f-equal-form): domain chốt AI GÁNH + TỈ TRỌNG; chia bao nhiêu mỗi
 * người do `ExpenseService.computeShares` (split-engine + `distributeWithRemainder`) tính — MỘT NGUỒN công
 * thức tiền, đồng dư có chủ. Form KHÔNG gửi shareMinor/fundPortionMinor (contract `refineExpenseSplitInvariants`
 * CẤM với fund_flow_allocated) và KHÔNG gửi payerMemberId ở đường F-* (contract CẤM — quỹ trả 100%).
 */
import { DomainError } from "../errors.js";
import {
  decodeRows,
  validateFieldValue,
  validateListField,
  type SlotFieldSpec,
  type SlotOption,
  type FormState,
} from "./form-slot-filling.js";
import {
  EXP_ADHOC_NAME_MAX_LENGTH,
  EXP_ADHOC_PG_BIGINT_MAX,
  EXP_SOURCE,
  EXP_SOURCE_ALLOCATED,
  type ExpFormSplit,
} from "./exp-adhoc-form.js";

/* ─── intentCode + specVersion (khớp catalog + router + FORM_SPEC_CATALOG EXACT) ─────────────────── */

/** intentCode EXP-F-RATIO (quỹ trả, chia gánh nặng theo % định trước). */
export const EXP_F_RATIO_INTENT_CODE = "EXP-F-RATIO";
/** intentCode EXP-F-EXCLUDE (quỹ trả, chia đều NHƯNG loại trừ một số người). */
export const EXP_F_EXCLUDE_INTENT_CODE = "EXP-F-EXCLUDE";
/** intentCode EXP-P-ADVANCE-EQUAL (một thành viên ứng túi, cả nhóm chia đều, mọi người hoàn người ứng). */
export const EXP_P_ADVANCE_EQUAL_INTENT_CODE = "EXP-P-ADVANCE-EQUAL";
/** intentCode EXP-P-PAY-FOR-ONE (A trả hộ ĐÚNG MỘT người B; B nợ A toàn bộ). */
export const EXP_P_PAY_FOR_ONE_INTENT_CODE = "EXP-P-PAY-FOR-ONE";

export const EXP_F_RATIO_SPEC_VERSION = 1;
export const EXP_F_EXCLUDE_SPEC_VERSION = 1;
export const EXP_P_ADVANCE_EQUAL_SPEC_VERSION = 1;
export const EXP_P_PAY_FOR_ONE_SPEC_VERSION = 1;

/* ─── Field key ──────────────────────────────────────────────────────────────────────────────────── */

/** Key field LIST (0249) mang % gánh của từng thành viên (EXP-F-RATIO). */
export const EXP_F_RATIO_SHARES_KEY = "ratioShares";
/** Cột con: thành viên (member_picker — options thành viên THẬT). */
export const EXP_F_RATIO_MEMBER_COLUMN = "memberId";
/** Cột con: % gánh (text — validate số ở domain; KHÔNG money vì % KHÔNG phải tiền). */
export const EXP_F_RATIO_PERCENT_COLUMN = "percent";
/** Key field multi_choice NGƯỜI BỊ LOẠI khỏi chia (EXP-F-EXCLUDE — khớp scenario "D không dự"). */
export const EXP_F_EXCLUDE_EXCLUDED_KEY = "excludedMembers";
/** Key field NGƯỜI ỨNG TIỀN (member_picker) — dùng chung EXP-P-*. */
export const EXP_P_PAYER_KEY = "payerMemberId";
/** Key field NGƯỜI GÁNH (multi_choice) — EXP-P-ADVANCE-EQUAL. */
export const EXP_P_BEARERS_KEY = "bearers";
/** Key field NGƯỜI ĐƯỢC TRẢ HỘ (member_picker — ĐÚNG MỘT người) — EXP-P-PAY-FOR-ONE. */
export const EXP_P_BENEFICIARY_KEY = "beneficiaryMemberId";

/* ─── Thứ tự field (tất-định — test khoá thứ tự này là contract nghiệp vụ) ───────────────────────── */

/** RATIO: quỹ → tên → tiền → list(% mỗi người). */
export const EXP_F_RATIO_FIELD_ORDER = ["fundId", "name", "amount", EXP_F_RATIO_SHARES_KEY] as const;
/** EXCLUDE: quỹ → tên → tiền → chọn NGƯỜI BỊ LOẠI (còn lại chia đều). */
export const EXP_F_EXCLUDE_FIELD_ORDER = [
  "fundId",
  "name",
  "amount",
  EXP_F_EXCLUDE_EXCLUDED_KEY,
] as const;
/** ADVANCE-EQUAL: quỹ → tên → tiền → NGƯỜI ỨNG → NGƯỜI GÁNH (chia đều). */
export const EXP_P_ADVANCE_EQUAL_FIELD_ORDER = [
  "fundId",
  "name",
  "amount",
  EXP_P_PAYER_KEY,
  EXP_P_BEARERS_KEY,
] as const;
/** PAY-FOR-ONE: quỹ → tên → tiền → NGƯỜI ỨNG → NGƯỜI ĐƯỢC TRẢ HỘ. */
export const EXP_P_PAY_FOR_ONE_FIELD_ORDER = [
  "fundId",
  "name",
  "amount",
  EXP_P_PAYER_KEY,
  EXP_P_BENEFICIARY_KEY,
] as const;

/**
 * 🔴 TỔNG % BẮT BUỘC = 100 (fail-loud — KHÔNG tự chuẩn hoá ngầm). Chuẩn hoá ngầm (vd Σ=90 → scale lên
 * 100) sẽ ÂM THẦM đổi gánh nặng tiền so với điều user gõ ⇒ CẤM. Lệch ⇒ DomainError (map 400).
 */
export const EXP_F_RATIO_PERCENT_TOTAL = 100;

/**
 * Số chữ số thập phân TỐI ĐA của % — mirror `scaleFractionalWeight` (engine scale weight ×10^4). Vượt ⇒
 * engine ném 422; domain chốt TRƯỚC ⇒ 400 tại biên (fail-fast, không để rơi xuống engine).
 */
export const EXP_F_RATIO_PERCENT_MAX_DECIMALS = 4;

/** % hợp lệ: số thập phân KHÔNG âm, tối đa 4 chữ số thập phân (vd "40", "20.5", "33.3333"). */
const PERCENT_PATTERN = /^\d+(\.\d{1,4})?$/;

/* ─── Payload ────────────────────────────────────────────────────────────────────────────────────── */

/** Payload chung 4 biến thể — nối vào CreateExpenseInput (fundId/name/amountMinor + split). */
export interface ExpSplitVariantPayload {
  readonly fundId: string;
  readonly name: string;
  /** STRING số nguyên dương minor (B1 — service parse → BigInt). */
  readonly amountMinor: string;
  readonly split: ExpFormSplit;
}

/* ─── Field builders ─────────────────────────────────────────────────────────────────────────────── */

/**
 * EXP-F-RATIO: [fund_picker → text(name) → money(amount) → list(ratioShares)[member_picker + text(%)]].
 * Cột % dùng `text` (KHÔNG `money`): % KHÔNG phải tiền — dùng money sẽ ép "số nguyên minor" ⇒ chặn mất
 * "20.5%" hợp lệ. Domain validate dạng số + Σ=100 (dưới).
 */
export function buildExpFRatioFields(
  fundOptions: readonly SlotOption[],
  memberOptions: readonly SlotOption[],
): SlotFieldSpec[] {
  return [
    { key: "fundId", type: "fund_picker", required: true, options: [...fundOptions] },
    { key: "name", type: "text", required: true },
    { key: "amount", type: "money", required: true },
    {
      key: EXP_F_RATIO_SHARES_KEY,
      type: "list",
      required: true,
      columns: [
        {
          key: EXP_F_RATIO_MEMBER_COLUMN,
          type: "member_picker",
          required: true,
          options: [...memberOptions],
        },
        { key: EXP_F_RATIO_PERCENT_COLUMN, type: "text", required: true },
      ],
    },
  ];
}

/**
 * EXP-F-EXCLUDE: [fund_picker → text(name) → money(amount) → multi_choice(excludedMembers)].
 * Chốt §6: chọn "ai KHÔNG gánh" (loại trừ — khớp scenario "D không dự"), phần còn lại chia ĐỀU.
 */
export function buildExpFExcludeFields(
  fundOptions: readonly SlotOption[],
  memberOptions: readonly SlotOption[],
): SlotFieldSpec[] {
  return [
    { key: "fundId", type: "fund_picker", required: true, options: [...fundOptions] },
    { key: "name", type: "text", required: true },
    { key: "amount", type: "money", required: true },
    {
      key: EXP_F_EXCLUDE_EXCLUDED_KEY,
      type: "multi_choice",
      required: true,
      options: [...memberOptions],
    },
  ];
}

/**
 * EXP-P-ADVANCE-EQUAL: [fund_picker → text → money → member_picker(payer) → multi_choice(bearers)].
 * `payerMemberId` = NGƯỜI ỨNG TIỀN TÚI (chủ nợ). member_picker (ĐÚNG 1 người — validateFieldValue chốt
 * value ∈ options, single value). `bearers` = ai chia đều gánh (mặc định cả nhóm, KỂ CẢ người ứng — người
 * ứng tự gánh phần mình, fanout KHÔNG sinh nợ tự-nợ-mình).
 */
export function buildExpPAdvanceEqualFields(
  fundOptions: readonly SlotOption[],
  memberOptions: readonly SlotOption[],
): SlotFieldSpec[] {
  return [
    { key: "fundId", type: "fund_picker", required: true, options: [...fundOptions] },
    { key: "name", type: "text", required: true },
    { key: "amount", type: "money", required: true },
    { key: EXP_P_PAYER_KEY, type: "member_picker", required: true, options: [...memberOptions] },
    { key: EXP_P_BEARERS_KEY, type: "multi_choice", required: true, options: [...memberOptions] },
  ];
}

/**
 * EXP-P-PAY-FOR-ONE: [fund_picker → text → money → member_picker(payer) → member_picker(beneficiary)].
 * ĐÚNG MỘT người hưởng ⇒ member_picker (single value) — KHÔNG multi_choice (multi cho phép nhiều ⇒ sai
 * usecase "trả hộ đúng một người").
 */
export function buildExpPPayForOneFields(
  fundOptions: readonly SlotOption[],
  memberOptions: readonly SlotOption[],
): SlotFieldSpec[] {
  return [
    { key: "fundId", type: "fund_picker", required: true, options: [...fundOptions] },
    { key: "name", type: "text", required: true },
    { key: "amount", type: "money", required: true },
    { key: EXP_P_PAYER_KEY, type: "member_picker", required: true, options: [...memberOptions] },
    {
      key: EXP_P_BENEFICIARY_KEY,
      type: "member_picker",
      required: true,
      options: [...memberOptions],
    },
  ];
}

/* ─── Split seams (nối ExpenseService.create — KHÔNG money-math) ─────────────────────────────────── */

/**
 * SEAM RATIO: source=fund_flow_allocated + rule='ratio' + participants=[{groupMemberId, weight: %}].
 * `weight` = % THÔ (engine `computeWeightedShares` scale ×10^4 rồi chia theo tỉ trọng ⇒ % là weight hợp
 * lệ, tỉ lệ bảo toàn). KHÔNG shareMinor (engine tính), KHÔNG payerMemberId (contract cấm với allocated).
 */
export function buildExpFRatioSplit(
  entries: readonly { readonly groupMemberId: string; readonly percent: number }[],
): ExpFormSplit {
  return {
    source: EXP_SOURCE_ALLOCATED,
    splitRule: "ratio",
    participants: entries.map((e) => ({ groupMemberId: e.groupMemberId, weight: e.percent })),
  };
}

/**
 * SEAM EXCLUDE: source=fund_flow_allocated + rule='equal' + participants = NGƯỜI CÒN LẠI (đã trừ người bị
 * loại). Người bị loại KHÔNG vào participants (thay vì isExempt) ⇒ engine chia đều trên tập còn lại,
 * Σshare = amount CHÍNH XÁC. Đây là nghĩa ĐÚNG của "D không dự" (D không gánh gì cả).
 */
export function buildExpFExcludeSplit(includedMemberIds: readonly string[]): ExpFormSplit {
  return {
    source: EXP_SOURCE_ALLOCATED,
    splitRule: "equal",
    participants: includedMemberIds.map((groupMemberId) => ({ groupMemberId })),
  };
}

/**
 * SEAM ADVANCE-EQUAL: source=peer + rule='equal' + participants = người gánh + `payerMemberId` = NGƯỜI ỨNG
 * KHAI TƯỜNG MINH (chủ nợ DebtEntry — fanout đọc CỘT NÀY, không suy từ actor). Quỹ KHÔNG chi (CASH net 0).
 */
export function buildExpPAdvanceEqualSplit(
  bearerMemberIds: readonly string[],
  payerMemberId: string,
): ExpFormSplit {
  return {
    source: EXP_SOURCE.PEER,
    splitRule: "equal",
    participants: bearerMemberIds.map((groupMemberId) => ({ groupMemberId })),
    payerMemberId,
  };
}

/**
 * SEAM PAY-FOR-ONE: source=peer + rule='equal' + participants = [beneficiary] DUY NHẤT ⇒ engine chia
 * "đều" trên 1 người = TOÀN BỘ amount về beneficiary ⇒ DebtEntry: beneficiary nợ payer TOÀN BỘ. payer
 * KHÔNG nằm trong participants (payer không gánh — A trả hộ B, A không tiêu gì).
 */
export function buildExpPPayForOneSplit(
  beneficiaryMemberId: string,
  payerMemberId: string,
): ExpFormSplit {
  return {
    source: EXP_SOURCE.PEER,
    splitRule: "equal",
    participants: [{ groupMemberId: beneficiaryMemberId }],
    payerMemberId,
  };
}

/* ─── Validate dùng chung ────────────────────────────────────────────────────────────────────────── */

/** Ném DomainError REQUIRED_FIELD_MISSING cho field bắt buộc còn trống. */
function assertPresent(value: string | null | undefined, field: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new DomainError("REQUIRED_FIELD_MISSING", `Thiếu thông tin bắt buộc: ${field}.`);
  }
  return value.trim();
}

/** fundId có + ∈ options (chống IDOR quỹ lạ ở LỚP domain — service re-verify RBAC THẬT lúc confirm). */
function validateFundId(formState: FormState, fundOptions: readonly SlotOption[]): string {
  const fundId = assertPresent(formState.fundId, "fundId");
  if (!new Set(fundOptions.map((o) => o.value)).has(fundId)) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      "Quỹ được chọn không nằm trong danh sách quỹ của bạn.",
    );
  }
  return fundId;
}

/** name non-empty (trim) + ≤ trần độ dài (mirror HTTP contract `.max(200)`). */
function validateName(formState: FormState): string {
  const name = assertPresent(formState.name, "name");
  if (name.length > EXP_ADHOC_NAME_MAX_LENGTH) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      `Tên khoản chi quá dài (tối đa ${EXP_ADHOC_NAME_MAX_LENGTH} ký tự).`,
    );
  }
  return name;
}

/** amount money minor no-float (validateFieldValue) + >0 + ≤ trần BIGINT (B1). */
function validateAmountMinor(formState: FormState): string {
  const amountRaw = assertPresent(formState.amount, "amount");
  const validation = validateFieldValue({ key: "amount", type: "money", required: true }, amountRaw);
  if (!validation.ok) {
    throw new DomainError("LEDGER_AMOUNT_NOT_POSITIVE", validation.message);
  }
  const amount = BigInt(validation.value);
  if (amount <= 0n) {
    throw new DomainError("LEDGER_AMOUNT_NOT_POSITIVE", "Số tiền phải lớn hơn 0.");
  }
  if (amount > EXP_ADHOC_PG_BIGINT_MAX) {
    throw new DomainError("LEDGER_AMOUNT_NOT_POSITIVE", "Số tiền vượt quá giới hạn cho phép.");
  }
  return validation.value;
}

/**
 * multi_choice → danh sách id ĐÃ chốt (∈ options + KHÔNG lặp + ≥1 nếu `requireNonEmpty`).
 * Lặp ⇒ một người gánh 2 suất = SAI TIỀN ⇒ fail-loud (validateFieldValue KHÔNG dedupe).
 */
function validateMemberIdList(
  formState: FormState,
  key: string,
  memberOptions: readonly SlotOption[],
  labelVi: string,
): string[] {
  const raw = assertPresent(formState[key], key);
  const validation = validateFieldValue(
    { key, type: "multi_choice", required: true, options: [...memberOptions] },
    raw,
  );
  if (!validation.ok) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      `${labelVi} không nằm trong danh sách thành viên của nhóm.`,
    );
  }
  const ids = validation.value.split(",").map((p) => p.trim()).filter(Boolean);
  if (ids.length === 0) {
    throw new DomainError("REQUIRED_FIELD_MISSING", `Cần chọn ${labelVi.toLowerCase()}.`);
  }
  const seen = new Set<string>();
  for (const id of ids) {
    if (seen.has(id)) {
      throw new DomainError("REQUIRED_FIELD_MISSING", `${labelVi}: thành viên bị lặp trong danh sách.`);
    }
    seen.add(id);
  }
  return ids;
}

/** member_picker (ĐÚNG 1 người) → id ĐÃ chốt ∈ options (IDOR lớp domain). */
function validateSingleMemberId(
  formState: FormState,
  key: string,
  memberOptions: readonly SlotOption[],
  labelVi: string,
): string {
  const raw = assertPresent(formState[key], key);
  const validation = validateFieldValue(
    { key, type: "member_picker", required: true, options: [...memberOptions] },
    raw,
  );
  if (!validation.ok) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      `${labelVi} không nằm trong danh sách thành viên của nhóm.`,
    );
  }
  return validation.value;
}

/* ─── Validate per usecase ───────────────────────────────────────────────────────────────────────── */

/**
 * EXP-F-RATIO (@UC9433) — validate CẢ BỘ → payload (fail-fast, KHÔNG partial).
 *
 * Thứ tự chốt (tất-định):
 *  1. fundId ∈ options (IDOR) · 2. name ≤ trần · 3. amount no-float/>0/≤trần BIGINT.
 *  4. list ratioShares: ≥1 dòng, member ∈ options (IDOR), % dạng số ≤4 thập phân, KHÔNG lặp member.
 *  5. 🔴 Σ% = 100 CHÍNH XÁC (so sánh trên SỐ NGUYÊN ×10^4 — KHÔNG float compare) ⇒ lệch = 400 fail-loud.
 *  6. 🔴 Mọi % > 0: % = 0 ⇒ người đó KHÔNG gánh gì mà vẫn nằm trong danh sách ⇒ nhập nhằng với EXCLUDE ⇒
 *     reject (buộc user bỏ hẳn người đó khỏi danh sách — ý định rõ ràng).
 */
export function validateExpFRatioForm(
  formState: FormState,
  fundOptions: readonly SlotOption[],
  memberOptions: readonly SlotOption[],
): ExpSplitVariantPayload {
  const fundId = validateFundId(formState, fundOptions);
  const name = validateName(formState);
  const amountMinor = validateAmountMinor(formState);

  // 4) list rows: validate qua protocol list 0249 (member ∈ options + cột required non-empty).
  const listField: SlotFieldSpec = {
    key: EXP_F_RATIO_SHARES_KEY,
    type: "list",
    required: true,
    columns: [
      {
        key: EXP_F_RATIO_MEMBER_COLUMN,
        type: "member_picker",
        required: true,
        options: [...memberOptions],
      },
      { key: EXP_F_RATIO_PERCENT_COLUMN, type: "text", required: true },
    ],
  };
  const rowsRaw = assertPresent(formState[EXP_F_RATIO_SHARES_KEY], EXP_F_RATIO_SHARES_KEY);
  const listValidation = validateListField(listField, rowsRaw);
  if (!listValidation.ok) {
    throw new DomainError("REQUIRED_FIELD_MISSING", listValidation.message);
  }

  const rows = decodeRows(listValidation.value);
  const seen = new Set<string>();
  const entries: { groupMemberId: string; percent: number }[] = [];
  // Σ% cộng dồn trên SỐ NGUYÊN ×10^4 (BigInt) — KHÔNG cộng float (0.1+0.2 ≠ 0.3 ⇒ Σ=100 sai lệch ngầm).
  let scaledTotal = 0n;
  for (const row of rows) {
    const memberId = String(row[EXP_F_RATIO_MEMBER_COLUMN] ?? "").trim();
    const percentRaw = String(row[EXP_F_RATIO_PERCENT_COLUMN] ?? "").trim();
    if (!PERCENT_PATTERN.test(percentRaw)) {
      throw new DomainError(
        "REQUIRED_FIELD_MISSING",
        `Tỷ lệ "%" phải là số không âm, tối đa ${EXP_F_RATIO_PERCENT_MAX_DECIMALS} chữ số thập phân (ví dụ: 40 hoặc 20.5).`,
      );
    }
    const scaled = scalePercentToInt(percentRaw);
    if (scaled <= 0n) {
      throw new DomainError(
        "REQUIRED_FIELD_MISSING",
        "Tỷ lệ phải lớn hơn 0 — bỏ hẳn người không gánh ra khỏi danh sách.",
      );
    }
    if (seen.has(memberId)) {
      throw new DomainError("REQUIRED_FIELD_MISSING", "Thành viên bị lặp trong danh sách tỷ lệ.");
    }
    seen.add(memberId);
    scaledTotal += scaled;
    entries.push({ groupMemberId: memberId, percent: Number(percentRaw) });
  }

  // 5) 🔴 Σ% = 100 CHÍNH XÁC (fail-loud — KHÔNG tự scale về 100: sẽ đổi ngầm gánh nặng tiền của user).
  const expectedTotal = BigInt(EXP_F_RATIO_PERCENT_TOTAL) * 10n ** BigInt(EXP_F_RATIO_PERCENT_MAX_DECIMALS);
  if (scaledTotal !== expectedTotal) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      `Tổng tỷ lệ phải đúng ${EXP_F_RATIO_PERCENT_TOTAL}% (hiện tại: ${formatScaledPercent(scaledTotal)}%).`,
    );
  }

  return { fundId, name, amountMinor, split: buildExpFRatioSplit(entries) };
}

/** "20.5" → 205000n (×10^4). Chỉ gọi SAU khi PERCENT_PATTERN đã chốt dạng — KHÔNG float. */
function scalePercentToInt(percent: string): bigint {
  const [intPart, fracPart = ""] = percent.split(".");
  const paddedFrac = fracPart.padEnd(EXP_F_RATIO_PERCENT_MAX_DECIMALS, "0");
  return BigInt(intPart ?? "0") * 10n ** BigInt(EXP_F_RATIO_PERCENT_MAX_DECIMALS) + BigInt(paddedFrac);
}

/** 205000n → "20.5" (bỏ số 0 thừa) — chỉ để DỰNG THÔNG BÁO lỗi, KHÔNG dùng cho phép tiền. */
function formatScaledPercent(scaled: bigint): string {
  const factor = 10n ** BigInt(EXP_F_RATIO_PERCENT_MAX_DECIMALS);
  const intPart = scaled / factor;
  const fracPart = scaled % factor;
  if (fracPart === 0n) return intPart.toString();
  const frac = fracPart.toString().padStart(EXP_F_RATIO_PERCENT_MAX_DECIMALS, "0").replace(/0+$/, "");
  return `${intPart}.${frac}`;
}

/**
 * EXP-F-EXCLUDE (@UC9434) — validate CẢ BỘ → payload.
 *
 * Thứ tự chốt: fundId ∈ options → name → amount → excludedMembers (∈ options, không lặp) → TÍNH tập CÒN
 * LẠI = memberOptions − excluded.
 *  - 🔴 Loại HẾT (không còn ai gánh) ⇒ reject (DoD "EXCLUDE bỏ hết member → reject").
 *  - `memberOptions` = thành viên ACTIVE THẬT của group chứa quỹ (service nạp) ⇒ tập còn lại LUÔN là
 *    người THẬT trong nhóm (không thể chèn người lạ — người lạ không nằm trong options ngay từ đầu).
 */
export function validateExpFExcludeForm(
  formState: FormState,
  fundOptions: readonly SlotOption[],
  memberOptions: readonly SlotOption[],
): ExpSplitVariantPayload {
  const fundId = validateFundId(formState, fundOptions);
  const name = validateName(formState);
  const amountMinor = validateAmountMinor(formState);

  const excluded = validateMemberIdList(
    formState,
    EXP_F_EXCLUDE_EXCLUDED_KEY,
    memberOptions,
    "Người được loại trừ",
  );
  const excludedSet = new Set(excluded);
  const included = memberOptions.map((o) => o.value).filter((id) => !excludedSet.has(id));
  if (included.length === 0) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      "Không còn ai gánh khoản chi — bạn đã loại trừ tất cả thành viên.",
    );
  }

  return { fundId, name, amountMinor, split: buildExpFExcludeSplit(included) };
}

/**
 * EXP-P-ADVANCE-EQUAL (@UC9435) — validate CẢ BỘ → payload.
 *
 * Thứ tự chốt: fundId → name → amount → payer ∈ options (IDOR — người ứng PHẢI thuộc nhóm của quỹ) →
 * bearers ∈ options, ≥1, không lặp.
 * 🔴 payerMemberId ĐI KÈM split (source=peer) ⇒ fanout dùng làm CHỦ NỢ. KHÔNG suy từ actor.
 */
export function validateExpPAdvanceEqualForm(
  formState: FormState,
  fundOptions: readonly SlotOption[],
  memberOptions: readonly SlotOption[],
): ExpSplitVariantPayload {
  const fundId = validateFundId(formState, fundOptions);
  const name = validateName(formState);
  const amountMinor = validateAmountMinor(formState);
  const payerMemberId = validateSingleMemberId(
    formState,
    EXP_P_PAYER_KEY,
    memberOptions,
    "Người ứng tiền",
  );
  const bearers = validateMemberIdList(
    formState,
    EXP_P_BEARERS_KEY,
    memberOptions,
    "Người gánh khoản chi",
  );

  return {
    fundId,
    name,
    amountMinor,
    split: buildExpPAdvanceEqualSplit(bearers, payerMemberId),
  };
}

/**
 * EXP-P-PAY-FOR-ONE (@UC9436) — validate CẢ BỘ → payload.
 *
 * Thứ tự chốt: fundId → name → amount → payer ∈ options → beneficiary ∈ options →
 * 🔴 beneficiary ≠ payer (DoD): tự-trả-hộ-mình = nợ chính mình = VÔ NGHĨA ⇒ reject fail-loud.
 */
export function validateExpPPayForOneForm(
  formState: FormState,
  fundOptions: readonly SlotOption[],
  memberOptions: readonly SlotOption[],
): ExpSplitVariantPayload {
  const fundId = validateFundId(formState, fundOptions);
  const name = validateName(formState);
  const amountMinor = validateAmountMinor(formState);
  const payerMemberId = validateSingleMemberId(
    formState,
    EXP_P_PAYER_KEY,
    memberOptions,
    "Người trả hộ",
  );
  const beneficiaryMemberId = validateSingleMemberId(
    formState,
    EXP_P_BENEFICIARY_KEY,
    memberOptions,
    "Người được trả hộ",
  );
  if (beneficiaryMemberId === payerMemberId) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      "Người được trả hộ phải KHÁC người trả hộ (không thể tự nợ chính mình).",
    );
  }

  return {
    fundId,
    name,
    amountMinor,
    split: buildExpPPayForOneSplit(beneficiaryMemberId, payerMemberId),
  };
}
